import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import back_app from '../../lib/back_app';

import xdate from '../../../../../util/date.js';

interface Asset {
  id: string;
  name: string;
  asset_type: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  ts?: {insert: Date, modified: Date};
}

interface Props {
  data: any;
  onChange: (field: string, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

function AssetPreview({ asset, isSelected, onClick }: { asset: Asset; isSelected: boolean; onClick: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const isVideo = asset.mime_type?.startsWith('video/');

  const handleMouseEnter = () => {
    if (videoRef.current && isVideo) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current && isVideo) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <>
      <div
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative bg-white rounded-xl border-2 overflow-hidden cursor-pointer transition-all group ${
          isSelected ? 'border-blue-500 shadow-lg scale-105' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        {}
        <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center relative overflow-hidden">
          {isVideo ? (
            <video
              ref={videoRef}
              src={asset.file_url}
              className="w-full h-full object-contain"
              muted
              loop
            />
          ) : (
            <img
              src={asset.file_url}
              alt={asset.name}
              className="w-full h-full object-contain"
            />
          )}

          {}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowPreview(true);
              }}
              className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all"
            >
              Preview
            </button>
          </div>

          {}
          <div className="absolute top-2 right-2">
            <span className="px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded">
              {asset.asset_type === 'video_ad' ? '📺 Video' : '🖼️ Banner'}
            </span>
          </div>

          {}
          {isSelected && (
            <div className="absolute top-2 left-2">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
          )}
        </div>

        {}
        <div className="p-3">
          <h4 className="font-semibold text-gray-900 text-sm mb-1 truncate">
            {asset.name}
          </h4>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{(asset.file_size / 1024 / 1024).toFixed(2)} MB</span>
            <span>{asset.ts?.insert ? xdate.fmt_lbl_short(asset.ts.insert) : ''}</span>
          </div>
        </div>
      </div>

      {}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl">
            <button
              onClick={() => setShowPreview(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>

            <div className="bg-black rounded-xl overflow-hidden">
              {isVideo ? (
                <video
                  src={asset.file_url}
                  controls
                  autoPlay
                  className="w-full"
                />
              ) : (
                <img
                  src={asset.file_url}
                  alt={asset.name}
                  className="w-full"
                />
              )}
            </div>

            <div className="mt-4 text-white text-center">
              <h3 className="text-xl font-bold">{asset.name}</h3>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function CampaignAssets({ data, onChange, onNext, onBack }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');

  const goUpload = () => {
    const sp = new URLSearchParams(location.search);
    sp.set('step', '2');
    const return_to = encodeURIComponent(location.pathname + '?' + sp.toString());
    navigate(`/brand/upload-asset?return_to=${return_to}`);
  };

  
  const selectedAssetIds = data.selected_asset_ids || [];

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const result = await back_app.basset_list();
      if (!result.err && Array.isArray(result)) {
        const validAssets = result.filter(
          (a: Asset) => a.asset_type === 'video_ad' || a.asset_type === 'banner'
        );
        setAssets(validAssets);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAssets = filterType === 'all'
    ? assets
    : assets.filter(a => a.asset_type === filterType);

  const handleNext = () => {
    if (selectedAssetIds.length === 0) {
      alert('Please select at least one asset for your campaign');
      return;
    }
    onNext();
  };

  
  const handleAssetToggle = (assetId: string) => {
    const newSelectedIds = selectedAssetIds.includes(assetId)
      ? selectedAssetIds.filter((id: string) => id !== assetId)
      : [...selectedAssetIds, assetId];
    
    console.log('🎨 Asset Selection Changed:', {
      assetId,
      previousCount: selectedAssetIds.length,
      newCount: newSelectedIds.length,
      selectedIds: newSelectedIds
    });
    
    onChange('selected_asset_ids', newSelectedIds);
    
    
    if (newSelectedIds.length > 0) {
      
      const selectedAsset = assets.find(a => a.id === assetId);
      if (selectedAsset) {
        
        const formats = selectedAsset.asset_type === 'video_ad' ? ['break'] : ['logo'];
        onChange('allowed_ad_formats', formats);
        console.log('📋 Updated allowed_ad_formats:', formats);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Select Campaign Assets
        </h3>
        <p className="text-gray-600">
          Choose one or more assets for this campaign. Creators can select one asset per video.
        </p>
      </div>

      {}
      {selectedAssetIds.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div>
              <p className="font-bold text-gray-900">
                {selectedAssetIds.length} {selectedAssetIds.length === 1 ? 'asset' : 'assets'} selected
              </p>
              <p className="text-sm text-gray-600">
                Creators will be able to choose from these assets
              </p>
            </div>
          </div>
        </div>
      )}

      {}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setFilterType('all')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
            filterType === 'all'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All Assets ({assets.length})
        </button>
        <button
          onClick={() => setFilterType('video_ad')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
            filterType === 'video_ad'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          📺 Videos ({assets.filter(a => a.asset_type === 'video_ad').length})
        </button>
        <button
          onClick={() => setFilterType('banner')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
            filterType === 'banner'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          🖼️ Banners ({assets.filter(a => a.asset_type === 'banner').length})
        </button>
      </div>

      {}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 mt-4">Loading assets...</p>
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
          <div className="text-6xl mb-4">📁</div>
          <h4 className="text-lg font-bold text-gray-900 mb-2">
            No assets found
          </h4>
          <p className="text-gray-600 mb-6">
            Upload your first {filterType === 'video_ad' ? 'video ad' : filterType === 'banner' ? 'banner' : 'asset'} to get started
          </p>
          <button
            onClick={goUpload}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
          >
            Upload Asset
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAssets.map((asset) => (
            <AssetPreview
              key={asset.id}
              asset={asset}
              isSelected={selectedAssetIds.includes(asset.id)}
              onClick={() => handleAssetToggle(asset.id)}
            />
          ))}
        </div>
      )}

      {}
      {assets.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                Need more assets?
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                Upload additional videos or banners to use in campaigns
              </p>
            </div>
            <button
              onClick={goUpload}
              className="px-4 py-2 bg-white border-2 border-blue-500 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-all whitespace-nowrap"
            >
              Upload New
            </button>
          </div>
        </div>
      )}

      {}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
        <button
          type="button"
          onClick={onBack}
          className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={selectedAssetIds.length === 0}
          className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue to Targeting →
        </button>
      </div>
    </div>
  );
}